import { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ScenarioGenerator.module.css';
import { useToast } from '../../../hooks/useToast';
import { usePromptModels } from '../hooks/usePromptModels';
import type { PromptModel } from '../hooks/usePromptModels';
import { SeedancePresetActorPicker } from './SeedancePresetActorPicker';
import type { CastingActor, SeedancePresetActorItem } from '../state/types';

type Props = {
  // 選択対象: Seedance 系モデルなら BytePlus preset、それ以外は通常AI俳優。
  isSeedance: boolean;
  // 選択上限 (= scenarios.character_count 由来)。現在の選択数がこれを超えている
  // 場合は呼び出し側で cap を引き上げて渡す。
  cap: number;
  initialCastings: CastingActor[];
  initialPresetActors: SeedancePresetActorItem[];
  onApply: (castings: CastingActor[], presetActors: SeedancePresetActorItem[]) => void;
  onClose: () => void;
  /** 指定すると「入れ替え」モード (docs/features/scenario-actor-replace-plan.md §3.4)。
   *  この配列位置だけを単一選択で差し替える。cap 判定はスキップし、既に配列内に
   *  ある俳優 (この位置を除く) は選択不可にする。選択即 onApply を呼んで閉じる
   *  ため「適用」ボタンは出さない。置換マッピング自体はここでは保持しない
   *  (サーバーが更新前後の配列差分から導出する。§3.1)。 */
  replaceIndex?: number;
};

/**
 * シナリオ情報モーダルから開く AI俳優の差し替えピッカー。
 * setup フェーズの CastingSection と同じカード UI で、選択順 = 登場人物A/B/C。
 * ここでの選択はローカル state に留め、「適用」でシナリオ情報モーダルの編集
 * state へ書き戻す (シナリオへの保存はシナリオ情報モーダルの「保存」で行う)。
 */
export function ScenarioActorSwapModal({
  isSeedance, cap, initialCastings, initialPresetActors, onApply, onClose, replaceIndex,
}: Props) {
  const toast = useToast();
  const [castings, setCastings] = useState<CastingActor[]>(initialCastings);
  const [presetActors, setPresetActors] = useState<SeedancePresetActorItem[]>(initialPresetActors);
  const isReplaceMode = replaceIndex !== undefined;

  // 通常AI俳優一覧 (Seedance のときは fetch しない)。フィルタ無しの全件。
  const castingModelsQuery = usePromptModels({}, { enabled: !isSeedance });

  const isMultiEnabled = !isReplaceMode && cap > 1;
  const selectedCastingIds = new Set(castings.map((c) => c.id));
  const castingOrderById = new Map(castings.map((c, i) => [c.id, i]));

  const handleToggleCasting = (model: PromptModel) => {
    if (selectedCastingIds.has(model.id)) {
      setCastings(castings.filter((c) => c.id !== model.id));
      return;
    }
    const nextActor: CastingActor = {
      id: model.id,
      display_name: model.display_name,
      face_image_public_url: model.face_image_public_url,
      character_sheet_public_url: model.character_sheet_public_url,
      gender: model.gender,
      min_age: model.min_age,
      max_age: model.max_age,
      ethnicity: model.ethnicity,
    };
    if (cap <= 1) {
      setCastings([nextActor]);
      return;
    }
    if (castings.length >= cap) {
      toast.error(`登場人物数の上限（${cap}人）に達しています`);
      return;
    }
    setCastings([...castings, nextActor]);
  };

  const handleTogglePreset = (actor: SeedancePresetActorItem) => {
    if (presetActors.some((a) => a.id === actor.id)) {
      setPresetActors(presetActors.filter((a) => a.id !== actor.id));
      return;
    }
    if (cap <= 1) {
      setPresetActors([actor]);
      return;
    }
    if (presetActors.length >= cap) {
      toast.error(`登場人物数の上限（${cap}人）に達しています`);
      return;
    }
    setPresetActors([...presetActors, actor]);
  };

  // 置換モード: 単一選択・選択即適用。既にこの位置以外にある俳優は選択不可
  // (並べ替え誤認の防止。§3.4)。置換マッピングは FE で保持せず、更新後の配列
  // だけを onApply へ渡す (サーバーが更新前後の配列差分から位置置換を導出する)。
  const handleSelectReplacementCasting = (model: PromptModel) => {
    if (replaceIndex === undefined) return;
    const existingIdx = castingOrderById.get(model.id);
    if (existingIdx !== undefined && existingIdx !== replaceIndex) return;
    const nextActor: CastingActor = {
      id: model.id,
      display_name: model.display_name,
      face_image_public_url: model.face_image_public_url,
      character_sheet_public_url: model.character_sheet_public_url,
      gender: model.gender,
      min_age: model.min_age,
      max_age: model.max_age,
      ethnicity: model.ethnicity,
    };
    const next = [...castings];
    next[replaceIndex] = nextActor;
    onApply(next, presetActors);
  };

  const handleSelectReplacementPreset = (actor: SeedancePresetActorItem) => {
    if (replaceIndex === undefined) return;
    const existingIdx = presetActors.findIndex((a) => a.id === actor.id);
    if (existingIdx !== -1 && existingIdx !== replaceIndex) return;
    const next = [...presetActors];
    next[replaceIndex] = actor;
    onApply(castings, next);
  };

  const selectedCount = isSeedance ? presetActors.length : castings.length;
  const replaceLetter = replaceIndex !== undefined ? String.fromCharCode(65 + replaceIndex) : null;
  // 置換モードでの選択不可 ID 集合 (この位置以外に既にある俳優)。
  const castingDisabledIds = new Set(
    isReplaceMode ? castings.filter((_, i) => i !== replaceIndex).map((c) => c.id) : [],
  );
  const presetDisabledIds = new Set(
    isReplaceMode ? presetActors.filter((_, i) => i !== replaceIndex).map((a) => a.id) : [],
  );

  // シナリオ情報モーダルの上に重ねる二段目のモーダル。同じ z-index の overlay でも
  // DOM 順 (後から body へ portal) で手前に描画される。
  return createPortal(
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth: 720, width: 'min(720px, calc(100vw - 32px))' }}>
        <div className="modal-header">
          <h3 className="modal-title">
            {replaceLetter ? `登場人物${replaceLetter} の俳優を入れ替え` : 'AI俳優を変更'}
          </h3>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
        <div className="modal-body">
          {isMultiEnabled && (
            <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', marginBottom: 8 }}>
              登場人物数に合わせて最大{cap}人まで選択できます（{selectedCount}/{cap}）
            </div>
          )}
          {isReplaceMode && (
            <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', marginBottom: 8 }}>
              選ぶとすぐに入れ替わります。すでに他の登場人物として選択中の俳優は選べません。
            </div>
          )}
          {isSeedance ? (
            <SeedancePresetActorPicker
              selectedActorIds={new Set(presetActors.map((a) => a.id))}
              orderById={new Map(presetActors.map((a, i) => [a.id, i]))}
              isMultiEnabled={isMultiEnabled}
              onToggleActor={isReplaceMode ? handleSelectReplacementPreset : handleTogglePreset}
              onClear={() => setPresetActors([])}
              genderFilter=""
              ageFilter={null}
              disabledActorIds={presetDisabledIds}
              hideNoneOption={isReplaceMode}
            />
          ) : (
            <div className={styles.castingCarousel}>
              <div className={`${styles.castingScrollContainer} ${styles.castingScrollMagnify}`}>
                <ul className={styles.castingGrid}>
                  {!isReplaceMode && (
                    <li>
                      <button
                        type="button"
                        className={`${styles.castingCard} ${styles.castingCardNone} ${castings.length === 0 ? styles.castingCardSelected : ''}`}
                        onClick={() => setCastings([])}
                        aria-pressed={castings.length === 0}
                      >
                        <div className={styles.castingImageNone}>
                          <img src="/anonyface.png" alt="" className={styles.castingNoneIcon} style={{ opacity: 0.5 }} />
                        </div>
                        <div className={styles.castingMeta}>指定しない</div>
                      </button>
                    </li>
                  )}
                  {castingModelsQuery.isLoading ? (
                    <li className={styles.castingLoading}>モデルを取得中…</li>
                  ) : castingModelsQuery.isError ? (
                    <li className={styles.castingError}>モデルの取得に失敗しました</li>
                  ) : castingModelsQuery.data?.map((model) => {
                    const isSelected = selectedCastingIds.has(model.id);
                    const orderIdx = castingOrderById.get(model.id);
                    const personLetter = (isMultiEnabled && isSelected && orderIdx !== undefined)
                      ? String.fromCharCode(65 + orderIdx)
                      : null;
                    const isDisabled = castingDisabledIds.has(model.id);
                    return (
                      <li key={model.id}>
                        <button
                          type="button"
                          className={`${styles.castingCard} ${styles.castingCardActor} ${isSelected ? styles.castingCardSelected : ''}`}
                          onClick={() => (isReplaceMode ? handleSelectReplacementCasting(model) : handleToggleCasting(model))}
                          disabled={isDisabled}
                          title={isDisabled ? '既に他の登場人物に割り当てられています' : undefined}
                          style={isDisabled ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                          aria-pressed={isSelected}
                        >
                          {personLetter && (
                            <span className={styles.castingExclusiveBadge} style={{ left: 4, right: 'auto', background: 'rgba(79, 70, 229, 0.9)' }}>
                              {`登場人物${personLetter}`}
                            </span>
                          )}
                          {model.is_exclusive && (
                            <span className={styles.castingExclusiveBadge}>専属</span>
                          )}
                          <div className={styles.castingImage}>
                            {model.face_image_public_url ? (
                              <img src={model.face_image_public_url} alt={model.display_name} />
                            ) : model.image_url ? (
                              <img src={model.image_url} alt={model.display_name} />
                            ) : (
                              <div className={styles.castingImagePlaceholder} />
                            )}
                          </div>
                          <div className={styles.castingMeta}>{model.display_name}</div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: isReplaceMode ? 'flex-start' : 'space-between', padding: '12px 16px' }}>
          <button type="button" className="btn-cancel" onClick={onClose}>
            キャンセル
          </button>
          {!isReplaceMode && (
            <button
              type="button"
              className="btn-modal"
              onClick={() => onApply(castings, presetActors)}
            >
              適用
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
